import { useShippingCountries } from "@/hooks/shops/useCountries";
import { useTrans } from "@/utils/useTrans";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/v2-components/ui/dropdown-menu";
import { useEffect, useMemo, useRef, useState } from "react";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import { cn } from "@/libs/utils";
import MagnifierIcon from "@/v2-components/core/icon/magnifier-icon";
import CountryFlag from "@/components/CountryFlag";
import { ShippingCountry } from "@/libs/getShippingCountries";

// Safari/WebKit은 setPointerCapture가 input의 native text-selection drag를 막는 quirk가 있음
const isWebKit = () =>
  typeof navigator !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export function SelectCountry({
  countryCode,
  setCountryCode,
}: {
  countryCode: string;
  setCountryCode: (countryCode: string) => void;
}) {
  const { t } = useTrans(["gnb"]);
  const { data, isPending } = useShippingCountries();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onUp = () => {
      isDraggingRef.current = false;
    };
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mouseup", onUp);
      // Esc/unmount로 드래그 중 cleanup되어도 다음 open에서 stale true가 안 남도록 reset
      isDraggingRef.current = false;
    };
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const items = listRef.current?.querySelectorAll("[data-country-item]");
    items?.[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  // Safari/WebKit은 native drag-selection이 preventDefault에 민감해서
  // 방어 코드가 오히려 드래그 자체를 깨뜨림. 전체 fix layer를 Chromium/Firefox에만 적용.
  const fixEnabled = !isWebKit();

  // 드래그 중 Radix가 content.focus()로 input focus를 빼앗는 것을 막는다.
  const blockWhileDragging = (e: { preventDefault: () => void }) => {
    if (fixEnabled && isDraggingRef.current) e.preventDefault();
  };

  // 드래그 중 마우스가 item 위로 이동할 때 input focus를 유지한다.
  const preserveInputFocus = (e: React.PointerEvent) => {
    if (fixEnabled && document.activeElement === inputRef.current) {
      e.preventDefault();
    }
  };

  const allCountries = useMemo(
    () =>
      Object.values(data ?? {})
        .flat()
        .sort((a, b) =>
          a.englishCountryName.localeCompare(b.englishCountryName),
        ),
    [data],
  );

  const selectedCountry = allCountries.find(
    (c) => c.countryCode === countryCode,
  );

  const filtered = useMemo(
    () =>
      query.trim()
        ? allCountries.filter((c) => {
            const q = query.trim().toLowerCase();
            if (c.searchText) {
              return c.searchText.includes(q);
            }
            return (
              c.koreanCountryName.toLowerCase().includes(q) ||
              c.ownCountryName.toLowerCase().includes(q) ||
              c.englishCountryName.toLowerCase().includes(q) ||
              c.countryCode.toLowerCase().includes(q)
            );
          })
        : allCountries,
    [allCountries, query],
  );

  const grouped = filtered.reduce<Record<string, ShippingCountry[]>>(
    (acc, c) => {
      const key = (c.englishCountryName?.[0] ?? "#").toUpperCase();
      (acc[key] ??= []).push(c);
      return acc;
    },
    {},
  );

  const groupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const total = filtered.length;
    if (total === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      setHighlightedIndex((prev) => (prev < total - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : total - 1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      e.stopPropagation();
      const item = filtered[highlightedIndex];
      if (item) {
        setCountryCode(item.countryCode);
        setQuery("");
        setOpen(false);
      }
    } else {
      e.stopPropagation();
    }
  };

  if (isPending) {
    return (
      <div className="w-full rounded border border-coolgray-20 bg-white px-4 py-3 text-sm text-coolgray-40">
        {t("Loading...")}
      </div>
    );
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      // modal=false: 드래그가 content 바깥으로 나가도 native text-selection 유지
      // Safari는 fix layer 전체를 안 쓰므로 기본값(modal=true) 유지
      modal={fixEnabled ? false : undefined}
    >
      <DropdownMenuTrigger className="w-full rounded border border-coolgray-20 bg-white text-left hover:border-coolgray-40">
        <div className="flex w-full items-center justify-between px-4 py-5">
          <div className="flex flex-1 items-center gap-x-2.5">
            <CountryFlag countryCode={countryCode} />
            <div className="text-coolgray-90">
              {selectedCountry?.ownCountryName ?? t("Select Country")}
            </div>
          </div>
          <ChevronDownIcon
            className={cn(
              "size-6 shrink-0 text-coolgray-50 transition-transform",
              open && "rotate-180",
            )}
          />
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        variant="select-like"
        className="border-px z-[200] flex w-[var(--radix-dropdown-menu-trigger-width)] flex-col overflow-hidden rounded border-coolgray-20 bg-white !p-0 shadow-md"
        sideOffset={6}
        onPointerLeave={blockWhileDragging}
        onPointerMove={blockWhileDragging}
        onFocusOutside={blockWhileDragging}
        onInteractOutside={blockWhileDragging}
      >
        {/* 검색 */}
        <div className="sticky top-0 bg-white p-4">
          <div className="relative">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("Search for Country/Region")}
              className="w-full rounded bg-coolgray-10 px-4 py-4 pr-9 text-base text-coolgray-90 outline-none"
              onKeyDown={handleKeyDown}
              onMouseDown={() => {
                isDraggingRef.current = true;
              }}
              // Safari/WebKit은 setPointerCapture가 input의 native text-selection drag를
              // 막아버리는 quirk가 있어 Chromium/Firefox에서만 적용
              onPointerDown={(e) => {
                if (isWebKit()) return;
                try {
                  e.currentTarget.setPointerCapture(e.pointerId);
                } catch {}
              }}
              onPointerUp={(e) => {
                if (isWebKit()) return;
                try {
                  if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                  }
                } catch {}
              }}
            />
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <MagnifierIcon />
            </div>
          </div>
        </div>

        {/* 국가리스트 */}
        <div ref={listRef} className="h-full flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-4 text-center text-base text-coolgray-30">
              {t("No results found")}
            </div>
          ) : (
            (() => {
              let flatIndex = 0;
              return groupKeys.map((key) => (
                <div key={key} className="mb-4">
                  <div className="px-4 text-xs font-500 text-coolgray-60">
                    {key}
                  </div>
                  {grouped[key].map((c, index) => {
                    const currentFlatIndex = flatIndex++;
                    const selected = c.countryCode === countryCode;
                    const isLast = index === grouped[key].length - 1;
                    const highlighted = currentFlatIndex === highlightedIndex;

                    return (
                      <DropdownMenuItem
                        key={c.countryCode}
                        data-country-item
                        onSelect={() => {
                          setCountryCode(c.countryCode);
                          setQuery("");
                          setOpen(false);
                        }}
                        onPointerMove={preserveInputFocus}
                        onPointerEnter={preserveInputFocus}
                        className={cn(
                          "cursor-pointer px-4 hover:bg-coolgray-10",
                          highlighted && "bg-coolgray-10",
                        )}
                      >
                        <CountryRow
                          className={isLast ? "border-b-0" : "border-b"}
                          countryCode={c.countryCode}
                          countryName={c.ownCountryName}
                          selected={selected}
                        />
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              ));
            })()
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const CountryRow = ({
  countryCode,
  countryName,
  className,
  selected,
}: {
  countryCode: string;
  countryName: string;
  className?: string;
  selected: boolean;
}) => {
  return (
    <div
      className={`flex w-full items-center justify-between border-coolgray-10 py-3.5 ${className ? "border-b-0" : "border-b"}`}
    >
      <div className="flex grow items-center gap-2.5">
        <CountryFlag countryCode={countryCode} />
        <div className="text-base text-coolgray-90">{countryName}</div>
      </div>

      {selected && <CheckSVG />}
    </div>
  );
};

const CheckSVG = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 16.4L6 12.4L7.4 11L10 13.6L16.6 7L18 8.4L10 16.4Z"
      fill="#21272A"
    />
  </svg>
);
